import { AudioPresets, VideoPresets } from 'livekit-client';
import type { RoomOptions } from 'livekit-client';

import type { CallKind } from '@/shared/services/api/types';

/**
 * Réglages d'un appel, écrits une fois. Tout ici sert un réseau 3G chargé,
 * pas la meilleure image possible :
 *
 * - **la voix d'abord** : Opus à 24 kb/s (préréglage `speech`), avec DTX (rien
 *   ne part pendant les silences, soit presque la moitié d'une conversation)
 *   et RED (chaque paquet porte une copie du précédent : une perte de paquets
 *   ne fait plus de trou dans la phrase). Annulation d'écho, réduction du
 *   bruit et gain automatique : un appel se passe rarement au calme ;
 * - **l'image ensuite** : 360p au plus, 450 kb/s, 20 images par seconde, avec
 *   une seconde couche à 180p (`simulcast`) que le serveur envoie à qui ne
 *   peut pas recevoir mieux, sans que l'émetteur ait rien à faire ;
 * - `adaptiveStream` : chacun ne reçoit que la taille de l'image qu'il
 *   affiche (la vignette de soi, une tuile de groupe) ; `dynacast` : une
 *   couche que personne ne regarde n'est même pas encodée ;
 * - quand le réseau ne suit plus, le serveur coupe d'abord l'image et garde
 *   la voix : l'écran le dit (« vidéo en pause »).
 */
export const CALL_VIDEO_MAX_BITRATE = 450_000;
export const CALL_VIDEO_MAX_FRAMERATE = 20;

export function callRoomOptions(kind: CallKind): RoomOptions {
  return {
    adaptiveStream: true,
    dynacast: true,
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
    videoCaptureDefaults: {
      resolution: VideoPresets.h360.resolution,
      facingMode: 'user',
    },
    publishDefaults: {
      audioPreset: AudioPresets.speech,
      dtx: true,
      red: true,
      ...(kind === 'VIDEO'
        ? {
            videoEncoding: {
              maxBitrate: CALL_VIDEO_MAX_BITRATE,
              maxFramerate: CALL_VIDEO_MAX_FRAMERATE,
            },
            simulcast: true,
            videoSimulcastLayers: [VideoPresets.h180],
            // Sur un lien qui se dégrade, garder la fluidité plutôt que la
            // netteté : un visage qui bouge se lit mieux qu'une photo nette.
            degradationPreference: 'maintain-framerate',
          }
        : {}),
    },
  };
}

/**
 * Le temps qu'on laisse à l'autre pour revenir quand il disparaît de la pièce
 * en pleine communication à deux (tunnel, ascenseur) avant de raccrocher.
 * Le serveur de diffusion, lui, le garde déjà une vingtaine de secondes.
 */
export const REMOTE_RETURN_GRACE_MS = 20_000;

/** Combien de temps l'écran montre « Appel terminé » avant de se refermer. */
export const ENDED_SCREEN_MS = 1800;
